import { Box } from "@bcc-ui/ui-components";
import { Buffer } from "buffer";
import React, { useEffect, useState } from "react";
import { getUA, isAndroid, isIOS } from "react-device-detect";
import { useTranslation } from "react-i18next";

import Footer from "./Footer";
import Header from "./Header";
import style from "./page.scss";
import AlertDialog from "components/alert-dialog/AlertDialog";
import Modal from "components/modal/Modal";
import Heading from "components/typography/heading/Heading";
import { DownloadPageConfigurationResponse } from "domain/downloadPage";
import { initializeBrowserLanguage } from "services/language/languageRepository";
import buttons from "styles/buttons.scss";
import { logger } from "utils/logging";

import blanccoLogo from "assets/images/logo/blanccoDSAppIconPlaceholder.svg";

import testIds from "testIds.json";

const SUBDOMAIN = process.env.GLOBAL_URL ? new URL(process.env.GLOBAL_URL).hostname.split(".")[0] : "dev";
const ENVIRONMENT = SUBDOMAIN === "portal" ? "prod" : SUBDOMAIN;

interface Props {
    licenseKey: string;
    storeName?: string | null;
    custom?: string | null;
    configuration: DownloadPageConfigurationResponse;
}

const Page = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [disableContinue, setDisableContinue] = useState(true);
    const [disableCopy, setDisableCopy] = useState(false);
    const [showCopyButtonAlert, setShowCopyButtonAlert] = useState(false);

    const storeName = props.storeName ? decodeURIComponent(props.storeName ?? "") : null;
    const custom = props.custom ? decodeURIComponent(props.custom ?? "") : null;

    const isChrome = getUA.includes("Chrome");

    const downloadConfiguration = () => {
        let configString = `pin=${props.licenseKey}|environment=${ENVIRONMENT}`;
        if (props.storeName || props.custom) {
            if (props.storeName) {
                configString += `|store_name=${storeName}`;
            }
            if (props.custom) {
                configString += `|custom=${custom}`;
            }
            return configString;
        }

        return configString;
    };

    const encodedDownloadConfiguration = Buffer.from(downloadConfiguration()).toString("base64");

    const writeToClipboard = async (): Promise<boolean> => {
        try {
            if (isChrome) {
                const permission = await navigator.permissions.query({ name: "clipboard-write" as PermissionName });
                if (permission.state === "denied") {
                    return false;
                }
            }

            await navigator.clipboard.writeText(encodedDownloadConfiguration);
            return true;
        } catch (err) {
            logger.error("Clipboard write failed", err);
            return false;
        }
    };

    const copyToClipboard = async () => {
        const writeSuccess = await writeToClipboard();
        if (!writeSuccess) {
            setShowModal(true);
            return;
        }

        try {
            if (isChrome) {
                const readPermission = await navigator.permissions.query({ name: "clipboard-read" as PermissionName });
                if (readPermission.state === "denied") {
                    setShowModal(true);
                    return;
                }
            }

            const clipboardContent = await navigator.clipboard.readText();
            if (clipboardContent === encodedDownloadConfiguration) {
                downloadApp();
            } else {
                logger.error("Clipboard write verification failed");
                setShowModal(true);
            }
        } catch (readErr) {
            logger.error("Unable to verify clipboard write", readErr);
            setShowModal(true);
        }
    };

    const showDefaultAppIcon = (e: React.ChangeEvent<HTMLImageElement>) => {
        e.target.onerror = null;
        e.target.src = blanccoLogo;
    };

    const downloadApp = async () => {
        setDisableContinue(true);
        setDisableCopy(false);

        if (props.licenseKey !== "") {
            let firebaseLink = "";
            if (isAndroid) {
                // If the Browser Agent includes HuaweiBrowser && HMSCore then use Huawei Firebase link.
                if (getUA.includes("HuaweiBrowser") && getUA.includes("HMSCore")) {
                    firebaseLink = props.configuration.huaweiFirebase;
                } else {
                    firebaseLink = props.configuration.androidFirebaseLink;
                }
            }

            if (isIOS) {
                firebaseLink = props.configuration.iosFirebaseLink;
            }

            if (firebaseLink && props.licenseKey != "") {
                firebaseLink = firebaseLink.replace("{PIN_NUMBER}", props.licenseKey);
                const isStoreLink = firebaseLink.includes("play.google.com") || firebaseLink.includes("apps.apple.com");
                if (!isStoreLink) {
                    firebaseLink = addUriEncodedParam(firebaseLink, "store_name", storeName);
                    firebaseLink = addUriEncodedParam(firebaseLink, "custom", custom);
                    firebaseLink = addUriEncodedParam(firebaseLink, "environment", ENVIRONMENT);
                }

                window.location.href = firebaseLink;
            }
        }
    };

    const addUriEncodedParam = (url: string, fieldName: string, fieldValue: string | null | undefined) => {
        if (!fieldValue) {
            return url;
        }
        return `${url}&${fieldName}=${fieldValue}`;
    };

    useEffect(() => {
        initializeBrowserLanguage();
    }, []);

    useEffect(() => {
        if (props.configuration.autoForward) {
            downloadApp();
        }
    }, [props.configuration.autoForward]);

    return (
        <div className={style.downloadPage}>
            <Header logo={props.configuration.headerLogo} backgroundColor={props.configuration.primaryColor} />
            <div className={style.container}>
                <div className={style.appIcon}>
                    <img
                        src={props.configuration.appIcon}
                        onError={showDefaultAppIcon}
                        data-testid={testIds.workArea.downloadPage.appIcon}
                    />
                </div>
                <Box paddingTop={"Sm"} paddingBottom={"Lg"} horizontalAlignment="Center">
                    <Heading variant={"SUBTITLE_2"} tag={"div"} testId={testIds.workArea.downloadPage.appTitle}>
                        {props.configuration.title}
                    </Heading>
                </Box>
                <div className={style.buttonContainer}>
                    <button
                        id="copyButton"
                        data-clipboard-text={encodedDownloadConfiguration}
                        onClick={() => {
                            copyToClipboard();
                        }}
                        className={buttons.primaryButton}
                        style={
                            props.configuration.primaryColor
                                ? { backgroundColor: props.configuration.primaryColor }
                                : undefined
                        }
                        data-testid={testIds.workArea.downloadPage.downloadAppButton}
                    >
                        {t("DownloadPage.downloadAppButton")}
                    </button>
                </div>
                <Modal
                    isOpen={showModal}
                    hideModal={() => {
                        setShowModal(false);
                        setDisableContinue(true);
                        setDisableCopy(false);
                    }}
                >
                    <div className={style.modalContainer}>
                        <div className={style.modalAppIcon}>
                            <img
                                src={props.configuration.appIcon}
                                onError={showDefaultAppIcon}
                                data-testid={testIds.workArea.downloadPage.appIcon}
                            />
                            <Heading tag={"div"} variant={"SUBTITLE_1"} disableTopSpacing disableBottomSpacing>
                                {t("DownloadPage.errorMessages.clipboardCopyFailure")}
                            </Heading>
                        </div>
                        {t("DownloadPage.errorMessages.clipboardCopyInstructions")}
                        <textarea
                            id="encodedDownloadConfiguration"
                            value={encodedDownloadConfiguration}
                            readOnly
                            onFocus={(e) => e.target.select()}
                            onCopy={() => {
                                logger.debug("User has manually copied text");
                                setDisableContinue(false);
                                setDisableCopy(true);
                            }}
                        />
                        <div className={style.buttonContainer}>
                            <button
                                id="copyButton"
                                onClick={() => {
                                    writeToClipboard().then((success) => {
                                        if (success) {
                                            logger.debug("Copy succeeded");
                                            setDisableContinue(false);
                                            setDisableCopy(true);
                                        } else {
                                            logger.debug("Copy failed");
                                            setDisableCopy(false);
                                            setShowCopyButtonAlert(true);
                                        }
                                    });
                                }}
                                disabled={disableCopy}
                                className={buttons.secondaryButton}
                                data-testid={testIds.workArea.downloadPage.copyButton}
                            >
                                {t("Common.copyToClipboard")}
                            </button>
                            <button
                                id="continueButton"
                                onClick={() => {
                                    setShowModal(false);
                                    downloadApp();
                                }}
                                disabled={disableContinue}
                                className={buttons.primaryButton}
                            >
                                {t("Common.continue")}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
            <AlertDialog
                isOpen={showCopyButtonAlert}
                hideModal={() => {
                    setShowCopyButtonAlert(false);
                    setDisableContinue(true);
                    setDisableCopy(true);
                }}
                headingText={t("Common.copyButtonFailedText")}
                primaryButton={{
                    text: t("Common.continue"),
                    action: () => {
                        setShowCopyButtonAlert(false);
                        setDisableContinue(true);
                        setDisableCopy(true);
                    },
                }}
                type={"ERROR"}
                body={t("DownloadPage.errorMessages.copyButtonFailedBody")}
            />
            <Footer />
        </div>
    );
};

export default Page;
